import ts from 'typescript';
import { SANDBOX_COMPILER_OPTIONS } from '../compilerConfig';
import { normalizeProjectPath } from './paths';

/** Where the generated topology files sit in a project tree. */
export interface TopologyPaths {
  shared: string;
  clientGame: string;
  clientArea: string;
  serverGame: string;
  serverArea: string;
  clientRegistry: string;
  serverRegistry: string;
  /** The web client `gotti dev` serves — `index.html` loads it. */
  clientEntry: string;
}

export const DEFAULT_TOPOLOGY_PATHS: TopologyPaths = {
  shared: 'src/shared/sandboxTopology.ts',
  clientGame: 'src/client/Games/SandboxGame.ts',
  clientArea: 'src/client/Areas/SandboxArea.ts',
  serverGame: 'src/server/Games/SandboxGame.ts',
  serverArea: 'src/server/Areas/SandboxArea.ts',
  clientRegistry: 'src/client/systems.generated.ts',
  serverRegistry: 'src/server/systems.generated.ts',
  clientEntry: 'src/client/index.ts',
};

/**
 * `value` as a single-quoted TypeScript string literal — a person's file name may hold a quote, a backslash, even a
 * line break.
 */
export const quoted = (value: string): string =>
  `'${value.replace(/[\\']/g, '\\$&').replace(/[\n\r\u2028\u2029]/g, (ch) => `\\u${ch.charCodeAt(0).toString(16).padStart(4, '0')}`)}'`;

/**
 * `path`, or — when a person's file already uses it — the same name with the next free numeric suffix before `.ts`.
 * `taken` is compared in its one spelling and without case: on a case-insensitive disk (macOS, Windows)
 * `games/sandboxgame.ts` and `Games/SandboxGame.ts` are one file.
 */
const freePath = (path: string, taken: ReadonlySet<string>): string => {
  const used = new Set([...taken].map((p) => normalizeProjectPath(p).toLowerCase()));
  if (!used.has(path.toLowerCase())) return path;
  const stem = path.replace(/\.ts$/, '');
  for (let n = 2; ; n++) {
    const candidate = `${stem}${n}.ts`;
    if (!used.has(candidate.toLowerCase())) return candidate;
  }
};

/** The topology's paths for a tree whose own files already use `taken`: a person's file is never overwritten. */
export function allocateTopologyPaths(taken: ReadonlySet<string>): TopologyPaths {
  const entries = Object.entries(DEFAULT_TOPOLOGY_PATHS).map(([key, path]) => [key, freePath(path, taken)]);
  return Object.fromEntries(entries) as unknown as TopologyPaths;
}

/**
 * The import specifier `fromFile` uses for `toFile`: relative, `./`- or `../`-prefixed, without `.ts`. A target in a
 * dot-folder (`./.wip/X`) keeps its `./` — a bare `.wip/X` would be read as a package name.
 */
export function moduleSpecifier(fromFile: string, toFile: string): string {
  const fromDir = normalizeProjectPath(fromFile).split('/').slice(0, -1);
  const to = normalizeProjectPath(toFile).replace(/\.ts$/, '').split('/');
  let shared = 0;
  while (shared < fromDir.length && shared < to.length - 1 && fromDir[shared] === to[shared]) shared++;
  const spec = [...fromDir.slice(shared).map(() => '..'), ...to.slice(shared)].join('/');
  return spec.startsWith('../') ? spec : `./${spec}`;
}

/**
 * A side's system files, in tree order and their one spelling: `.ts` files under `src/<side>/` whose path has a
 * lowercase `/systems/` folder — the sandbox runtime's rule — except what the runtime never loads as a system (specs,
 * tests, anything under `__tests__`) and type declarations. `shared/` systems were never registered, so they stay out.
 */
export function systemFilePaths(files: Record<string, string>, side: 'client' | 'server'): string[] {
  return Object.keys(files).map(normalizeProjectPath).filter((path) =>
    path.startsWith(`src/${side}/`) &&
    path.includes('/systems/') &&
    path.endsWith('.ts') &&
    !path.endsWith('.d.ts') &&
    !/\.(spec|test)\.ts$/.test(path) &&
    !path.includes('__tests__'),
  );
}

/** A property access or element access on `exports` (`exports.X`, `exports["X"]`): the export name, or `undefined`. */
const exportsMember = (node: ts.Node): string | undefined => {
  if (ts.isPropertyAccessExpression(node) && ts.isIdentifier(node.expression) && node.expression.text === 'exports') return node.name.text;
  if (ts.isElementAccessExpression(node) && ts.isIdentifier(node.expression) && node.expression.text === 'exports' && ts.isStringLiteral(node.argumentExpression)) {
    return node.argumentExpression.text;
  }
  return undefined;
};

/**
 * The names a module exports, in the order its CommonJS `exports` object gains them — the order the sandbox runtime's
 * `for…in` has always registered systems in. TypeScript's CommonJS emit hoists the names of exported values into one
 * `exports.D = exports.C = … = void 0` chain (assigned right to left), exports function declarations next, then the
 * rest as the module runs. Read from the emit without running it: assignments to `exports` and
 * `Object.defineProperty(exports, …)` outside any function body, in evaluation order. Names an `export * from` brings
 * in are only known when the module runs; the registry adds those after these.
 */
export function exportOrder(code: string): string[] {
  const emitted = ts.transpileModule(code, { compilerOptions: { ...SANDBOX_COMPILER_OPTIONS } }).outputText;
  const source = ts.createSourceFile('module.js', emitted, ts.ScriptTarget.Latest, false, ts.ScriptKind.JS);
  const names = new Set<string>();
  const visit = (node: ts.Node): void => {
    if (ts.isFunctionLike(node) || ts.isClassLike(node)) return;
    if (ts.isBinaryExpression(node) && node.operatorToken.kind === ts.SyntaxKind.EqualsToken) {
      const name = exportsMember(node.left);
      if (name !== undefined) {
        visit(node.right);
        names.add(name);
        return;
      }
    }
    if (ts.isCallExpression(node)) {
      ts.forEachChild(node, visit);
      const [target, key] = node.arguments;
      const callee = node.expression;
      if (
        ts.isPropertyAccessExpression(callee) && ts.isIdentifier(callee.expression) && callee.expression.text === 'Object' &&
        callee.name.text === 'defineProperty' && target && ts.isIdentifier(target) && target.text === 'exports' &&
        key && ts.isStringLiteral(key) && key.text !== '__esModule'
      ) {
        names.add(key.text);
      }
      return;
    }
    ts.forEachChild(node, visit);
  };
  source.statements.forEach(visit);
  return [...names];
}

/** A system file of the project: where it is and what it holds. */
export interface SystemSource {
  path: string;
  code: string;
}

/**
 * The registry the side's Area file imports: every function each system file exports — the sandbox runtime's rule —
 * files in tree order, each file's exports in the order the runtime registered them (`exportOrder`). An ES module
 * namespace lists its exports alphabetically, so the registry names them. The site rewrites this file whenever a
 * system file is added, renamed or removed, or changes what it exports; a stale list still registers every function
 * the file exports.
 */
export function emitSystemRegistry(side: 'client' | 'server', registryPath: string, systems: ReadonlyArray<SystemSource>): string {
  const areaFile = side === 'client' ? 'ClientAreaFile' : 'ServerAreaFile';
  const imports = systems.map(({ path }, i) => `import * as system${i} from ${quoted(moduleSpecifier(registryPath, path))};`);
  const entries = systems.map(({ code }, i) => `  inOrder(system${i}, [${exportOrder(code).map(quoted).join(', ')}]),`);
  return [
    `// Rewritten by the sandbox whenever a file under a systems/ folder is added, renamed or removed, or changes what it`,
    `// exports: every function those files export is a ${side} system, in load order. To register systems by hand, stop`,
    `// importing this file in the Area file and list them there.`,
    `import type { ${areaFile} } from '@gotti/${side}';`,
    ...imports,
    '',
    '/**',
    ' * The functions a system module exports, in the order the sandbox runtime has always registered them: `names`, the',
    ' * order its file declares them in, then anything else it exports (what an `export *` brings in).',
    ' */',
    'const inOrder = (module: object, names: string[]): unknown[] => {',
    '  const byName = module as Record<string, unknown>;',
    '  const listed = new Set(names);',
    '  return [...names, ...Object.keys(byName).filter((name) => !listed.has(name))]',
    '    .map((name) => byName[name])',
    "    .filter((value) => typeof value === 'function');",
    '};',
    '',
    `export const ${side}Systems = [`,
    ...entries,
    `].flat() as unknown as ${areaFile}['systems'];`,
    '',
  ].join('\n');
}

const HEADER = '// Generated by the sandbox when this project was created. It is your file: edit it freely.';

/** The shared ids, the two Game files and the two Area files: the topology the sandbox runtime synthesizes, as code. */
export function emitTopologyFiles(paths: TopologyPaths): Record<string, string> {
  const ids = (from: string) => quoted(moduleSpecifier(from, paths.shared));
  return {
    [paths.shared]: [
      HEADER,
      "// The ids this project's one game and one area go by — the Game and Area files and the playground use them.",
      `export const SANDBOX_GAME_TYPE = 'sandbox';`,
      `export const SANDBOX_AREA_TYPE = 'sandbox-area';`,
      `export const SANDBOX_GAME_ID = 'sandbox-game';`,
      `export const SANDBOX_AREA_ID = 'sandbox-area-1';`,
      '',
    ].join('\n'),
    [paths.clientGame]: [
      HEADER,
      `import type { ClientGameFile } from '@gotti/client';`,
      `import { SANDBOX_GAME_TYPE } from ${ids(paths.clientGame)};`,
      '',
      "// What every client system's $api gets from this game: the canvas the client draws on, its 2D context, the",
      '// element around it.',
      `declare module '@gotti/client' {`,
      '  interface ClientApiRegistry {',
      '    canvas: HTMLCanvasElement;',
      '    ctx: CanvasRenderingContext2D;',
      '    container: HTMLElement;',
      '  }',
      '}',
      '',
      'const SandboxGame: ClientGameFile = {',
      '  type: SANDBOX_GAME_TYPE,',
      '  isNetworked: true,',
      '  fixedTimeStep: true,',
      '  timeStep: 1 / 60,',
      '  // The systems live on the Area file, so they start when the client joins the area.',
      '  systems: [],',
      '  async api(_gameData?: unknown, context?: { canvas?: HTMLCanvasElement; container?: HTMLElement }) {',
      '    const canvas = context?.canvas;',
      '    if (!canvas) return {};',
      `    const ctx = canvas.getContext('2d');`,
      `    if (!ctx) throw new Error('sandbox: failed to get a 2D canvas context');`,
      '    return { canvas, ctx, container: context?.container ?? document.body };',
      '  },',
      '};',
      '',
      'export default SandboxGame;',
      '',
    ].join('\n'),
    [paths.clientArea]: [
      HEADER,
      `import type { ClientAreaFile } from '@gotti/client';`,
      `import { SANDBOX_AREA_TYPE } from ${ids(paths.clientArea)};`,
      `import { clientSystems } from ${quoted(moduleSpecifier(paths.clientArea, paths.clientRegistry))};`,
      '',
      'export const SandboxArea: ClientAreaFile = {',
      '  type: SANDBOX_AREA_TYPE,',
      '  systems: clientSystems,',
      '};',
      '',
      'export default SandboxArea;',
      '',
    ].join('\n'),
    [paths.serverGame]: [
      HEADER,
      `import type { ServerGameFile } from '@gotti/server';`,
      `import { SANDBOX_AREA_ID, SANDBOX_GAME_TYPE } from ${ids(paths.serverGame)};`,
      '',
      'const SandboxGame: ServerGameFile = {',
      '  type: SANDBOX_GAME_TYPE,',
      '  fixedTimeStep: true,',
      '  timeStep: 1 / 60,',
      '  // The systems live on the Area file.',
      '  systems: [],',
      '  // Every client starts in the one area.',
      '  async getInitialAreas() {',
      '    return [SANDBOX_AREA_ID];',
      '  },',
      '  async api() {',
      '    return {};',
      '  },',
      '};',
      '',
      'export default SandboxGame;',
      '',
    ].join('\n'),
    [paths.serverArea]: [
      HEADER,
      `import type { ServerAreaFile } from '@gotti/server';`,
      `import { SANDBOX_AREA_TYPE } from ${ids(paths.serverArea)};`,
      `import { serverSystems } from ${quoted(moduleSpecifier(paths.serverArea, paths.serverRegistry))};`,
      '',
      'export const SandboxArea: ServerAreaFile = {',
      '  type: SANDBOX_AREA_TYPE,',
      '  systems: serverSystems,',
      '  // `gotti dev` refuses to start an area with no answer to a join request — it needs allowAllJoins, serverOnly or',
      '  // an onJoinRequest handler. This one seats every client, as the sandbox does.',
      '  allowAllJoins: true,',
      '};',
      '',
      'export default SandboxArea;',
      '',
    ].join('\n'),
  };
}
